/**
 * cellSort — comparators shared by the Explorer's two sorting renderers
 * (`components/cells/GridView.jsx` and `components/cells/TableView.jsx`), which
 * each keep their own sort switch. Anything whose ordering rule is subtler than
 * "compare two numbers" belongs here so the two views cannot drift apart.
 */

/**
 * Milliseconds since epoch for the cell's catalogue-insertion date, or `null`
 * when the cell has none.
 *
 * This reads `createdAt` (when BATEMO ADDED the cell to the catalogue), NOT
 * `date` / `year` (when the MANUFACTURER released the cell). Both are legitimate
 * senses of "new" and users ask for both, so they are separate sort keys with
 * deliberately NO fallback between them: substituting one for the other would
 * answer a question nobody asked, convincingly and invisibly.
 *
 * Parsed to a number rather than compared as ISO strings: string comparison
 * happens to work while every row is formatted identically and stops working
 * the moment one carries a timezone offset or a different precision — i.e.
 * exactly when a backend change introduces it without anyone noticing.
 *
 * @param {{ createdAt?: string|null }} [cell]
 * @returns {number|null}
 */
export function addedDateSortValue(cell) {
  const raw = cell?.createdAt
  if (!raw) return null
  const ms = Date.parse(raw)
  return Number.isFinite(ms) ? ms : null
}

/**
 * Compare two values so that `null` (missing) always sorts LAST, in both
 * directions. A missing catalogue date must not be coerced to 0 (→ 1970, i.e.
 * "the oldest cell", which reads as data we do not have) nor to `Date.now()`
 * (→ "just added", which is an outright lie).
 *
 * Returns the FINAL comparator result — `order` is already applied, so callers
 * must return this directly instead of letting an outer `asc ? r : -r` flip the
 * nulls back to the front.
 *
 * @param {number|null} aVal
 * @param {number|null} bVal
 * @param {'asc'|'desc'} [order]
 * @returns {number}
 */

/** Cell-shape path per Ion/UI sort key. Keys mirror `SORT_KEYS` in
 *  backend/app/api/v1/chat/explorer_vocab.py. */
export const QUALITY_SORT_FIELDS = {
  aging: 'aging.CAP_loss_rel',
  batchVariation: 'batch.CAP_spread_std_rel',
}

/**
 * The value a quality sort orders on, or `null` when the cell has none.
 *
 * Returned SIGNED for aging, deliberately. `CAP_loss_rel` is negative when the
 * cell gained capacity during formation and positive when it lost some, and the
 * table column renders exactly that ("-2.00" vs. "+3.00"). Sorting on the
 * absolute value — which the free-plot axis does — would put a 2% gain and a 2%
 * loss side by side, so descending would no longer mean "worst first" and the
 * order would contradict the numbers in the very column being sorted.
 *
 * Fractions are kept as fractions: the ×100 the renderers apply is monotonic,
 * so it cannot change any ordering, and not doing it here keeps this function
 * free of display concerns.
 */
export function qualitySortValue(cell, key) {
  const path = QUALITY_SORT_FIELDS[key]
  if (!path) return null
  const [group, field] = path.split('.')
  const raw = cell?.[group]?.[field]
  // A withheld metric arrives as null with a band alongside it; a genuinely
  // absent one arrives as undefined. Both mean "we cannot order this cell".
  if (raw == null) return null
  const num = typeof raw === 'number' ? raw : parseFloat(raw)
  return Number.isFinite(num) ? num : null
}

/**
 * Compare two possibly-null values, with nulls last in BOTH directions.
 *
 * "In both directions" is the whole point and the easy thing to get wrong:
 * pushing nulls to the end and then negating for descending flips them back to
 * the front, so the first page of a descending sort would be nothing but cells
 * with no data. Missing data is not an extreme value — it is not on the scale at
 * all, so it belongs at the end whichever way the arrow points.
 *
 * Returns a comparator result with `order` ALREADY applied, so callers return it
 * straight from their switch rather than passing it through their own asc/desc
 * flip at the end.
 */
export function compareNullsLast(aVal, bVal, order = 'asc') {
  const aMissing = aVal == null
  const bMissing = bVal == null
  if (aMissing && bMissing) return 0
  if (aMissing) return 1
  if (bMissing) return -1
  const result = aVal < bVal ? -1 : aVal > bVal ? 1 : 0
  return order === 'asc' ? result : -result
}

/**
 * Whether the LOADED catalogue can support the `addedDate` sort at all.
 *
 * Only the primary `/explorer/v2` payload carries `created_at`
 * (`modules/explorerCellShape.js`). `MainAppPage`'s legacy fallback path
 * (`getProductList` + `batchGetCells` → `getCellNormalized`) builds cells from
 * the per-cell DETAIL response, which exposes `general.date` (manufacturer
 * release date) but no catalogue-insertion date — so on that path every cell's
 * `createdAt` is `null` and the comparator is a total tie. A sort control that
 * cannot reorder anything must not be offered: this predicate is what the
 * Explorer's sort dropdowns and the `?sort=addedDate` deep-link guard key off,
 * so the option disappears (and the param degrades visibly) exactly when the
 * data behind it is missing — reactively, per load, not behind a build flag.
 *
 * @param {Array<{ createdAt?: string|null }>} [cells]
 * @returns {boolean} true iff at least one loaded cell carries a parseable date
 */
export function hasAddedDateData(cells) {
  if (!Array.isArray(cells)) return false
  return cells.some((cell) => addedDateSortValue(cell) !== null)
}

/** True for the sort keys this module owns. */
export function isQualitySortKey(key) {
  return Object.prototype.hasOwnProperty.call(QUALITY_SORT_FIELDS, key)
}
